import React, { createContext, useContext, useState, FC } from 'react';

import { v4 as uuid4 } from 'uuid';
import ChevronDownIcon from '@components/chevrondownicon';
import Typography from '@components/typography';
import cn from 'classnames';
import SearchBar from '@components/searchbar/searchbar';

import '@styles/accordion.scss';
import { useDeviceDetect } from '@hooks/usedevicedetect';

export type AccordionType = 'normal' | 'hasvalue' | 'selectable';

type AccordionContent = string | string[] | AccordionItemValue[];
export interface AccordionItemValue {
  label: string;
  value?: string | string[];
  title?: string;
  checked?: boolean;
  id?: number;
  type?: AccordionType;
  dependants?: AccordionItemValue[];
  description?: string[];
  disabled?: boolean;
  tooltip?: React.ReactNode;
  isMobile?: boolean;
}

export interface AccordionItemData {
  index: number;
  title: string;
  content: AccordionContent;
  setSelectedOptions?: (itemId: number, title: string) => void;
  type?: AccordionType;
  openAccordionItem?: boolean;
  isMobile?: boolean;
}

interface IAccordionContext {
  active: number;
  toggle?: (index: number) => void;
}
const defaultState = {
  active: -1,
};

export interface AccordionProps {
  data: AccordionItemData[];
  isSearchable?: boolean;
  searchPlaceholder?: string;
  hasSelection?: boolean;
  setSelectedOptions?: (itemId: number, title: string) => void;
  className?: string;
  type: AccordionType;
}

export const AccordionContext = createContext<IAccordionContext>(defaultState);

const AccordionTitleValueItem: FC<AccordionItemValue> = ({
  label,
  value,
  id,
  checked,
  type,
  dependants,
  description,
  disabled,
  tooltip,
  isMobile,
}) => {
  const [selectedOption, setSelectedOption] = useState<boolean | undefined>(
    checked
  );
  const handleChange = () => {
    setSelectedOption(!selectedOption);
  };

  return (
    <div className="accordion__content-container">
      {type === 'selectable' && (
        <div className="accordion__content-container__value">
          <input
            name={uuid4()}
            className="option"
            value={id}
            type="radio"
            aria-label="Choose"
            defaultChecked={selectedOption}
            data-testid={`accordion-item-radio-${id}`}
            data-cy={`accordion-item-radio-${id}`}
            onChange={handleChange}
            onClick={handleChange}
            disabled={disabled}
            id={`accordion-item-radio-${id}`}
          />
        </div>
      )}
      <label
        className={cn(
          tooltip && !isMobile ? '' : 'accordion__content-container-value-text'
        )}
        data-testid={`accordion-item-text-${id}`}
        data-cy={`accordion-item-text-${id}`}
        htmlFor={`accordion-item-radio-${id}`}
      >
        <Typography
          variant="body1"
          size="rg"
          className={cn(
            'accordion__content-container-value-indicator',
            disabled && 'required'
          )}
          data-testid="row-label"
        >{`${type !== 'selectable' ? '-' : ''} ${label}`}</Typography>
      </label>
      {tooltip && <>{tooltip}</>}
      {Array.isArray(value) ? (
        <div className="accordion__content-container-value-list">
          {value.map((item, index) => {
            return (
              <Typography
                key={uuid4()}
                variant="body1"
                size="rg"
                data-testid={`row-value-${index}`}
              >
                {item}
              </Typography>
            );
          })}
        </div>
      ) : value ? (
        <span className="accordion__content-container-value">
          {' '}
          <Typography variant="button" size="sm" data-testid="row-value">
            {value}
          </Typography>
        </span>
      ) : null}
      {dependants && dependants.length > 0 ? (
        <div className="accordion__content-container-dependants">
          <Typography variant="caption" size="sm" weight="semi-bold">
            Some of the following options are free, whilst others are required
            at an extra cost
          </Typography>
          {dependants.map((dependant) => {
            return (
              <div
                className="accordion__content-container-dependants-item"
                key={uuid4()}
              >
                <Typography variant="body1" size="sm" data-testid="row-label">
                  {dependant.label}
                </Typography>
                <div className="accordion__content-container-dependants-item-values">
                  {dependant.value &&
                    Array.isArray(dependant.value) &&
                    dependant.value.map((item, index) => {
                      return (
                        <Typography
                          key={uuid4()}
                          variant="body1"
                          size="sm"
                          data-testid={`row-value-${index}`}
                        >
                          {item}
                        </Typography>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      {description && description.length > 0 ? (
        <div
          className="accordion__content-container-description"
          data-testid={`description-${id}`}
        >
          <Typography variant="caption" size="sm" weight="semi-bold">
            Below is a list of what is included in this option
          </Typography>
          <ul>
            {description.map((m) => {
              return (
                <li
                  className="accordion__content-container-description-item"
                  key={uuid4()}
                >
                  <Typography variant="body1" size="sm" data-testid="row-label">
                    {m}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const AccordionItem: FC<AccordionItemData> = ({
  index,
  title,
  content,
  type,
  setSelectedOptions,
  openAccordionItem,
  isMobile,
}) => {
  let indexPlus;

  const indexCount = (indexCurrent: number) => {
    indexPlus = indexCurrent + 1;
    return indexPlus;
  };

  const { active, toggle } = useContext(AccordionContext);

  const clickListItem = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    itemId: number | undefined
  ) => {
    if (itemId) {
      event.preventDefault();

      if (setSelectedOptions) {
        setSelectedOptions(itemId, title);
      }
    }
  };

  const eventHandler = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    indexCurrent: number
  ) => {
    e.preventDefault();
    toggle?.(indexCurrent);
  };

  return (
    <div className="accordion__container">
      <h3
        className="accordion__title"
        data-cy="accordion-item-title"
        data-testid="accordion-item-title"
      >
        <button
          type="button"
          data-testid="expand-button"
          data-cy="expand-button"
          onClick={(e) => eventHandler(e, index)}
          className={cn(
            'accordion__title-button',
            active === index && 'accordion__title-button--active'
          )}
          aria-expanded={active === index ? 'true' : 'false'}
          aria-controls={'acc-' + indexCount(index)}
          aria-disabled={active === index ? 'true' : 'false'}
        >
          <span
            className="accordion__title-button-text"
            data-testid="title-text"
          >
            {title}
          </span>
          <span
            className={cn(
              'accordion__title-button-icon',
              active === index ? 'rotate' : ''
            )}
          >
            <ChevronDownIcon />
          </span>
        </button>
      </h3>
      <div
        id={'acc-' + indexCount(index)}
        className={cn(
          'accordion__panel',
          ((active === index && active !== -1) || openAccordionItem) &&
            'accordion__panel--open'
        )}
        data-testid="accordion-item-panel"
      >
        {Array.isArray(content) ? (
          <ul className="accordion__content">
            {content.map((item) => (
              <li
                data-testid={typeof item === 'string' ? item : item.label}
                data-cy={
                  typeof item === 'string'
                    ? item
                    : `accordion-listitem-${item.id}`
                }
                key={uuid4()}
                className={cn(
                  'accordion__content-item',
                  type === 'selectable' && 'accordion__content-item-list'
                )}
                onClick={(event) =>
                  clickListItem(
                    event,
                    typeof item === 'string' ? undefined : item.id
                  )
                }
              >
                <AccordionTitleValueItem
                  label={typeof item === 'string' ? item : item.label}
                  value={typeof item === 'string' ? undefined : item.value}
                  id={typeof item === 'string' ? undefined : item.id}
                  checked={typeof item === 'string' ? undefined : item.checked}
                  type={type}
                  dependants={
                    typeof item === 'string' ? undefined : item.dependants
                  }
                  description={
                    typeof item === 'string' ? undefined : item.description
                  }
                  disabled={
                    typeof item === 'string' ? undefined : item.disabled
                  }
                  tooltip={typeof item === 'string' ? undefined : item.tooltip}
                  isMobile={isMobile}
                />
              </li>
            ))}
          </ul>
        ) : (
          <Typography variant="button" size="rg" data-testid="raw-text">
            {content}
          </Typography>
        )}
      </div>
    </div>
  );
};

const Accordion: FC<AccordionProps> = ({
  data = [],
  isSearchable = false,
  searchPlaceholder,
  setSelectedOptions,
  className = null,
  type,
}) => {
  const [active, setActive] = useState<number>(-1);
  const [searchText, setSearchText] = useState<string>('');

  const handleSearchTyping = (search: string) => {
    setSearchText(search);
  };

  const { isMobile } = useDeviceDetect();

  const searchInObjects = (item: AccordionItemData): boolean => {
    //check if exist in top values of object
    let exist = Object.values(item)
      .join('')
      .toLowerCase()
      .includes(searchText.toLocaleLowerCase());
    let existInSubitems = false;

    //if it already exists on the top no need deep searching
    if (!exist) {
      Object.values(item).forEach((subItem) => {
        if (Array.isArray(subItem)) {
          subItem.forEach((content) => {
            //to search in object array in the main object's value
            if (
              Object.values(content)
                .join('')
                .toLowerCase()
                .includes(searchText.toLocaleLowerCase())
            ) {
              existInSubitems = true;
            }
          });
        }
      });
    }

    return exist || existInSubitems;
  };

  const renderItems = () => {
    const filteredData = !searchText
      ? data
      : data.filter((item) => item && searchInObjects(item));

    return filteredData.length > 0 ? (
      filteredData.map((item, index) => (
        <AccordionItem
          key={uuid4()}
          index={index}
          title={item.title}
          content={item.content}
          setSelectedOptions={setSelectedOptions}
          type={type}
          openAccordionItem={searchText.length > 0}
          isMobile={isMobile}
        />
      ))
    ) : searchText ? (
      <div className="accordion__no-result">
        <Typography variant="button" size="rg" data-testid="no-result-text">
          No results found
        </Typography>
      </div>
    ) : null;
  };

  return (
    <AccordionContext.Provider
      value={{
        active,
        toggle: (index) => setActive((prev) => (prev === index ? -1 : index)),
      }}
    >
      <div
        className={cn('accordion', className)}
        data-testid="accordion-wrapper"
        data-cy="accordion-wrapper"
      >
        <section>
          {isSearchable && data.length > 0 && (
            <>
              <SearchBar
                placeHolder={searchPlaceholder}
                handleSearchTyping={handleSearchTyping}
                searchText={searchText}
              />
              {type !== 'selectable' && <div className="divider"></div>}
            </>
          )}
          {renderItems()}
        </section>
      </div>
    </AccordionContext.Provider>
  );
};

export default Accordion;
